import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, TrendingUp, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const TYPE_LABELS = {
  residential: "Residenziale",
  commercial: "Commerciale",
  land: "Terreno",
  industrial: "Industriale",
  other: "Altro",
};
const TYPE_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#64748b"];
const BUCKET_LABELS = {
  excellent: "Eccellenti (≥85)",
  good: "Buone (70-84)",
  fair: "Discrete (50-69)",
  weak: "Deboli (<50)",
};
const BUCKET_COLORS = { excellent: "#10b981", good: "#3b82f6", fair: "#f59e0b", weak: "#ef4444" };

function fmt(n) { return Intl.NumberFormat("it-IT").format(Math.round(Number(n) || 0)); }

export default function ClientDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/client-stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const typeData = stats ? Object.entries(stats.by_property_type || {}).map(([k, v]) => ({ name: TYPE_LABELS[k] || k, value: v })) : [];
  const bucketData = stats ? Object.entries(stats.opportunity_buckets || {}).map(([k, v]) => ({ name: BUCKET_LABELS[k], value: v, key: k })) : [];

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Le tue opportunità</h1>
          <p className="text-muted-foreground text-sm mt-1">Panoramica dell'archivio opportunità immobiliari off-market.</p>
        </div>
        <Button asChild data-testid="cta-explore-opportunities">
          <Link to="/properties" className="gap-2">Esplora l'archivio <ArrowRight className="w-4 h-4" /></Link>
        </Button>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Building2} label="Opportunità disponibili" value={stats?.total_opportunities} accent="text-blue-600" testid="kpi-total-opportunities" />
        <KPI icon={TrendingUp} label="Valore complessivo" value={stats ? `€ ${fmt(stats.total_value)}` : null} accent="text-emerald-600" testid="kpi-total-value" />
        <KPI icon={TrendingUp} label="Margine potenziale" value={stats ? `€ ${fmt(stats.total_margin)}` : null} accent="text-amber-600" testid="kpi-total-margin" />
        <KPI icon={Sparkles} label="ROI medio" value={stats ? `${stats.avg_roi}%` : null} accent="text-violet-600" testid="kpi-avg-roi" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-border/60 p-6">
          <div className="mb-4">
            <h3 className="font-heading text-lg font-medium">Opportunity Index — Distribuzione</h3>
            <p className="text-xs text-muted-foreground">Punteggio delle opportunità nell'archivio</p>
          </div>
          <div className="h-64">
            {stats ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bucketData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {bucketData.map((b) => (<Cell key={b.key} fill={BUCKET_COLORS[b.key]} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Skeleton className="w-full h-full" />}
          </div>
        </Card>

        <Card className="border-border/60 p-6">
          <h3 className="font-heading text-lg font-medium mb-4">Tipologie</h3>
          <div className="h-64">
            {stats ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40}>
                    {typeData.map((_, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Skeleton className="w-full h-full" />}
          </div>
        </Card>
      </div>

      {/* Top cities */}
      <Card className="border-border/60 p-6" data-testid="top-cities-card">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-medium">Città con più opportunità</h3>
        </div>
        {!stats ? <Skeleton className="h-24 w-full" /> : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {stats.top_cities.length === 0 && <p className="text-sm text-muted-foreground col-span-full">Nessuna città ancora disponibile.</p>}
            {stats.top_cities.map((c) => (
              <Link key={c.city} to={`/properties?q=${encodeURIComponent(c.city)}`} className="rounded-md border border-border/60 p-4 hover:border-primary/40 hover:bg-muted/40 transition-colors" data-testid={`city-${c.city}`}>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.city}</p>
                <p className="mt-1 font-heading text-2xl font-semibold tabular-nums">{c.count}</p>
                <p className="text-xs text-muted-foreground mt-1">opportunità</p>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="border-border/60 p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
          <p className="mt-3 font-heading text-3xl font-semibold tracking-tight tabular-nums">
            {value === null || value === undefined ? "—" : value}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-md bg-muted flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}
